// =============================================================================
// VINUM - Button Component
// Migrated from Flutter GFButton with EXACT variants
// =============================================================================

import React from 'react';
import { cn } from '@/lib/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      icon,
      iconPosition = 'left',
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = [
      'inline-flex items-center justify-center gap-2',
      'font-medium rounded-sm',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'active:scale-[0.98] transition-transform',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ];

    const variantStyles = {
      primary: [
        'bg-primary text-white',
        'hover:bg-primary-dark active:bg-primary-dark',
        'focus:ring-primary',
      ],
      secondary: [
        'bg-secondary text-white',
        'hover:bg-secondary-dark active:bg-secondary-dark',
        'focus:ring-secondary',
      ],
      outline: [
        'border border-border bg-transparent text-gray-700',
        'hover:bg-gray-50 active:bg-gray-100',
        'dark:border-dark-border dark:text-gray-300',
        'dark:hover:bg-gray-800 dark:active:bg-gray-700',
        'focus:ring-primary',
      ],
      ghost: [
        'bg-transparent text-gray-600',
        'hover:bg-gray-100 active:bg-gray-200',
        'dark:text-gray-400 dark:hover:bg-gray-800 dark:active:bg-gray-700',
        'focus:ring-primary',
      ],
      danger: [
        'bg-error text-white',
        'hover:bg-error-dark active:bg-error-dark',
        'focus:ring-error',
      ],
    };

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm h-8',
      md: 'px-4 py-2 text-base h-10',
      lg: 'px-6 py-3 text-base h-12',
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <>
            <span className="spinner spinner-sm" />
            <span>Caricamento...</span>
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && icon}
            {children}
            {icon && iconPosition === 'right' && icon}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
